<template>
  <div class="modal-wrapper">
    <div class="modal-backdrop-custom" @click="$emit('close')"></div>

    <div class="modal fade show modal-dialog-overlay" style="display: block;" tabindex="-1">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content shadow-lg">
          <!-- Modal Header -->
          <div class="modal-header bg-gradient-primary text-white">
            <div>
              <h5 class="modal-title mb-0 text-white">
                <i class="mdi mdi-shield-account me-2"></i>Manage Roles
              </h5>
              <small class="opacity-75">{{ category.categoryName }}</small>
            </div>
            <button @click="$emit('close')" type="button" class="btn-close btn-close-white"></button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body p-4">
            <!-- Search and Stats -->
            <div class="row mb-4">
              <div class="col-md-8">
                <div class="input-group">
                  <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
                  <input
                      v-model="searchQuery"
                      type="text"
                      class="form-control"
                  placeholder="Search roles by name or group..."
                  />
                  <button v-if="searchQuery" @click="searchQuery = ''" class="btn btn-outline-secondary" type="button">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card border-0 bg-primary bg-opacity-10">
                  <div class="card-body p-2 text-center">
                    <h5 class="mb-0 text-primary text-white">{{ selectedCountDisplay }} / {{ totalRoles }}</h5>
                    <small class="text-muted">{{ viewMode === 'navigation' ? 'Navigation Items Selected' : 'Roles Selected' }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <button @click="selectAllRoles" class="btn btn-sm btn-outline-primary me-2">
                  <i class="mdi mdi-checkbox-multiple-marked"></i> Select All
                </button>
                <button @click="deselectAllRoles" class="btn btn-sm btn-outline-secondary">
                  <i class="mdi mdi-checkbox-multiple-blank-outline"></i> Deselect All
                </button>
                <button
                  @click="showAssignedOnly = !showAssignedOnly"
                  class="btn btn-sm ms-2"
                  :class="showAssignedOnly ? 'btn-primary' : 'btn-outline-dark'"
                >
                  <i class="mdi mdi-filter-check"></i> {{ showAssignedOnly ? 'Showing Assigned Only' : 'Show Assigned Only' }}
                </button>
                <button
                  @click="viewMode = viewMode === 'navigation' ? 'all' : 'navigation'"
                  class="btn btn-sm ms-2"
                  :class="viewMode === 'navigation' ? 'btn-info' : 'btn-outline-info'"
                >
                  <i class="mdi mdi-view-list"></i> {{ viewMode === 'navigation' ? 'Navigation View' : 'All Roles View' }}
                </button>
              </div>
              <div>
                <span class="badge bg-info">{{ Object.keys(groupedRoles).length }} Groups</span>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="text-muted mt-2">Loading roles...</p>
            </div>

            <!-- Roles by Group -->
            <div v-else class="roles-container">
              <div v-for="(roles, categoryName) in filteredGroupedRoles" :key="categoryName" class="role-group mb-4">
                <div class="card">
                  <div class="card-header bg-light">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-0">
                          <i class="mdi mdi-folder-outline me-2"></i>
                          {{ categoryName }}
                          <span class="badge bg-secondary ms-2">{{ roles.length }}</span>
                        </h6>
                      </div>
                      <div>
                        <button @click="toggleGroupSelection(categoryName, roles)" class="btn btn-sm btn-link">
                          {{ isGroupFullySelected(roles) ? 'Deselect All' : 'Select All' }}
                        </button>
                        <button @click="toggleGroupExpand(categoryName)" class="btn btn-sm btn-link">
                          <i :class="expandedGroups.includes(categoryName) ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-if="expandedGroups.includes(categoryName)" class="card-body">
                    <div class="row">
                      <div v-for="role in roles" :key="role.key" class="col-md-6 col-lg-4 mb-2">
                        <div class="form-check">
                          <input
                              :checked="isItemSelected(role)"
                              @change="toggleItem(role, $event.target.checked)"
                              :id="'role-' + role.key"
                              class="form-check-input"
                              type="checkbox"
                              :disabled="!(role.ids && role.ids.length)"
                          />
                          <label :for="'role-' + role.key" class="form-check-label" :title="role.description">
                            {{ role.role_name }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="!loading && Object.keys(filteredGroupedRoles).length === 0" class="text-center py-5">
              <i class="mdi mdi-shield-off text-muted" style="font-size: 48px;"></i>
              <p class="text-muted mt-2">No roles found matching your search</p>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer bg-light">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div>
                <span class="text-muted">
                  <strong>{{ selectedRoles.length }}</strong> role(s) will be assigned to
                  <strong>{{ category.categoryName }}</strong>
                </span>
              </div>
              <div>
                <button @click="$emit('close')" type="button" class="btn btn-light me-2">
                  <i class="mdi mdi-close me-1"></i>Cancel
                </button>
                <button @click="saveRoles" type="button" class="btn btn-primary" :disabled="saving">
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="mdi mdi-content-save me-1"></i>
                  {{ saving ? 'Saving...' : 'Save Roles' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute, toast } from "@/api";

export default {
  name: "ManageRolesModal",
  props: {
    category: {
      type: Object,
      required: true
    },
    allRoles: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      selectedRoles: [],
      searchQuery: '',
      expandedGroups: [],
      showAssignedOnly: false,
      viewMode: 'all',
      GROUP_ORDER: [
        'Governor',
        'Dashboard',
        'County Secretary',
        'Staff Establishment',
        'County Attorney',
        'Public Service Board',
        'Attendance Management',
        'Leave Management',
        'Communication',
        'Document Management',
        'General HR Actions',
        'Skills Audit',
        'Finance',
        'Reports',
        'System Management',
        'Employee Self-service',
        'Uncategorized'
      ]
    };
  },
  computed: {
    normalizedAllRoles() {
      const seenIds = new Set();
      const out = [];
      (this.allRoles || []).forEach((role) => {
        const id = String(role?.id ?? '');
        if (!id) return;
        if (seenIds.has(id)) return;
        seenIds.add(id);
        out.push({ ...role, id });
      });
      return out;
    },
    permissionToMenuMap() {
      const out = {};
      let menus = [];
      try {
        menus = JSON.parse(sessionStorage.getItem("rbac_menus") || "[]");
      } catch (_) {}
      (Array.isArray(menus) ? menus : []).forEach((m) => {
        const code = String(m?.permission_code || "").trim().toUpperCase();
        if (!code) return;
        const active = !(m?.is_active === false || m?.is_active === 0 || String(m?.is_active).trim() === "0");
        if (!active) return;
        if (!out[code]) {
          out[code] = {
            title: String(m?.title || code),
            section: this.normalizeSectionName(String(m?.section_title || "")),
            route: String(m?.route || "")
          };
        }
      });
      const ensureAlias = (code, title, section) => {
        const c = String(code || "").trim().toUpperCase();
        if (!c || out[c]) return;
        out[c] = { title, section, route: "" };
      };
      ensureAlias("GENERAL DOCUMENTS", "General Documents", "Document Management");
      ensureAlias("PAYROLL REPORT", "Payroll CSV vs Register", "Reports");
      ensureAlias("UPLOAD PAYROLL", "Payroll CSV vs Register", "Reports");
      ensureAlias("REPORT_DEPARTMENT_WAGE_BILL", "Payroll CSV vs Register", "Reports");
      ensureAlias("REPORT_COUNTY_WAGE_BILL", "Payroll CSV vs Register", "Reports");
      // Skills Audit group (aligned with Navigation.vue — independent section)
      ensureAlias("EMPLOYEES SKILLS AUDITS", "Employees Skills Audits", "Skills Audit");
      ensureAlias("SKILLS AUDIT SKILLS INVENTORY", "Skills Inventory", "Skills Audit");
      ensureAlias("SKILLS AUDIT SKILL GAP", "Skill Gap", "Skills Audit");
      ensureAlias("SKILLS AUDIT MONITORING EVALUATION", "Monitoring and Evaluation", "Skills Audit");
      ensureAlias("SKILLS AUDIT REPORT", "Skills Audit Report", "Skills Audit");
      ensureAlias("SKILLS AUDIT SUPERVISOR ACTION", "Skills Audit Supervisor Action", "Skills Audit");
      ensureAlias("SKILLS AUDIT CHIEF OFFICER ACTION", "Skills Audit Chief Officer Action", "Skills Audit");
      ensureAlias("MY SKILLS AUDIT ASSESSMENT", "Skills Audit Assessment", "Employee Self-service");
      // Force canonical placement for communication/admin vs employee-self roles.
      out["ANNOUNCEMENTS"] = { title: "Announcements", section: "Communication", route: "announcements" };
      out["EVENTS"] = { title: "Events", section: "Communication", route: "hr-events" };
      out["MEETINGS"] = { title: "Meetings", section: "Communication", route: "hr-meetings" };
      out["MY ANNOUNCEMENTS"] = { title: "My Announcements", section: "Employee Self-service", route: "employee-announcements" };
      out["MY EVENTS"] = { title: "My Events", section: "Employee Self-service", route: "hr-my-events" };
      out["MY MEETINGS"] = { title: "My Meetings", section: "Employee Self-service", route: "employee-my-meetings" };
      return out;
    },
    navigationGroupedRoles() {
      const grouped = {};
      const codeToIds = {};
      this.normalizedAllRoles.forEach((role) => {
        const code = String(role?.role_name || "").trim().toUpperCase();
        if (!codeToIds[code]) codeToIds[code] = [];
        codeToIds[code].push(String(role.id));
      });
      this.normalizedAllRoles.forEach((role) => {
        const code = String(role?.role_name || "").trim().toUpperCase();
        const menu = this.permissionToMenuMap[code] || null;
        const categoryName = menu ? menu.section : this.resolveRoleGroup(role);
        const displayName = menu ? menu.title : String(role.role_name || code);
        const key = `${categoryName}::${displayName}`.toLowerCase();
        if (!grouped[categoryName]) grouped[categoryName] = [];
        let existing = grouped[categoryName].find((x) => x.key === key);
        if (!existing) {
          existing = {
            key,
            role_name: displayName,
            description: menu ? `Navigation: ${menu.title}` : (role.description || ""),
            ids: []
          };
          grouped[categoryName].push(existing);
        }
        existing.ids.push(String(role.id));
      });
      // Ensure key navigation items are visible even when menu metadata is inconsistent.
      this.upsertSyntheticNavigationItem(grouped, 'Document Management', 'General Documents', ['GENERAL DOCUMENTS'], codeToIds);
      this.upsertSyntheticNavigationItem(
        grouped,
        'Reports',
        'Payroll CSV vs Register',
        ['PAYROLL REPORT', 'UPLOAD PAYROLL', 'REPORT_DEPARTMENT_WAGE_BILL', 'REPORT_COUNTY_WAGE_BILL'],
        codeToIds
      );
      // Ensure these appear even if nav_menus metadata is incomplete.
      this.upsertSyntheticNavigationItem(grouped, 'Communication', 'Announcements', ['ANNOUNCEMENTS'], codeToIds);
      this.upsertSyntheticNavigationItem(grouped, 'Communication', 'Events', ['EVENTS'], codeToIds);
      this.upsertSyntheticNavigationItem(grouped, 'Communication', 'Meetings', ['MEETINGS'], codeToIds);
      this.upsertSyntheticNavigationItem(grouped, 'Employee Self-service', 'My Announcements', ['MY ANNOUNCEMENTS'], codeToIds);
      this.upsertSyntheticNavigationItem(grouped, 'Employee Self-service', 'My Events', ['MY EVENTS'], codeToIds);
      this.upsertSyntheticNavigationItem(grouped, 'Employee Self-service', 'My Meetings', ['MY MEETINGS'], codeToIds);
      Object.keys(grouped).forEach((k) => {
        grouped[k].sort((a, b) => String(a.role_name || '').localeCompare(String(b.role_name || '')));
      });
      return this.sortGrouped(grouped);
    },
    allGroupedRoles() {
      const grouped = {};
      this.normalizedAllRoles.forEach((role) => {
        const categoryName = this.resolveRoleGroup(role);
        if (!grouped[categoryName]) grouped[categoryName] = [];
        grouped[categoryName].push({
          key: `role-${role.id}`,
          role_name: String(role.role_name || ''),
          description: role.description || '',
          ids: [String(role.id)]
        });
      });
      Object.keys(grouped).forEach((k) => {
        grouped[k].sort((a, b) => String(a.role_name || '').localeCompare(String(b.role_name || '')));
      });
      return this.sortGrouped(grouped);
    },
    groupedRoles() {
      return this.viewMode === 'navigation' ? this.navigationGroupedRoles : this.allGroupedRoles;
    },

    filteredGroupedRoles() {
      const query = this.searchQuery.toLowerCase();
      const filtered = {};

      Object.keys(this.groupedRoles).forEach(categoryName => {
        const matchingRoles = this.groupedRoles[categoryName].filter(role => {
            const matchesSearch = !query ||
              role.role_name.toLowerCase().includes(query) ||
              categoryName.toLowerCase().includes(query) ||
              (role.description && role.description.toLowerCase().includes(query));
            const matchesAssigned = !this.showAssignedOnly || this.isItemSelected(role);
            return matchesSearch && matchesAssigned;
        });

        if (matchingRoles.length > 0) {
          filtered[categoryName] = matchingRoles;
        }
      });

      return this.sortGrouped(filtered);
    },

    totalRoles() {
      return Object.values(this.groupedRoles).reduce((sum, rows) => sum + rows.length, 0);
    },
    selectedCountDisplay() {
      if (this.viewMode !== 'navigation') return this.selectedRoles.length;
      let count = 0;
      Object.values(this.groupedRoles).forEach((rows) => {
        rows.forEach((row) => {
          if (this.isItemSelected(row)) count++;
        });
      });
      return count;
    }
  },
  mounted() {
    this.loadCategoryRoles();

    // Expand all groups by default
    this.$nextTick(() => {
      this.expandedGroups = Object.keys(this.groupedRoles);
    });
  },
  methods: {
    sortGrouped(grouped) {
      const out = {};
      this.GROUP_ORDER.forEach((name) => {
        if (grouped[name] && grouped[name].length) out[name] = grouped[name];
      });
      Object.keys(grouped)
        .filter((k) => !this.GROUP_ORDER.includes(k))
        .sort((a, b) => a.localeCompare(b))
        .forEach((k) => { out[k] = grouped[k]; });
      return out;
    },
    resolveRoleGroup(role) {
      const cat = String(role?.category || '').trim().toLowerCase();
      const name = String(role?.role_name || '').trim().toLowerCase();
      const code = String(role?.role_name || '').trim().toUpperCase();
      const text = `${cat} ${name}`;

      // Employee Self-service (codes starting MY / Apply / Clock / payslips / documents / typical ESS labels)
      if (
        /^MY /i.test(code) ||
        /^APPLY FOR |^ATTENDANCE RECORDS|^CLOCK |^SELF ASSESSMENT|^VIEW PAYSLIP|^TAX DOCUMENTS|^PAYROLL HISTORY|^LEAVE HISTORY|^LEAVE BALANCE|^LEAVE CALENDAR|^VIEW DOCUMENTS|^UPLOAD DOCUMENTS|^REDEPLOYMENT_EMPLOYEE/i.test(code) ||
        /\bmy dashboard\b|\bmy profile\b|\bmy leave\b|\bmy attendance\b|\bmy payroll\b|\bmy documents\b|\bmy announcements\b|\bmy events\b|\bmy meetings\b|\bmy warnings\b|\bmy training\b|\bmy performance\b|\bmy redeployment\b|\bmy wealth\b|\bemployee self/i.test(text)
      ) {
        return 'Employee Self-service';
      }

      // HR Skills Audit / reporting (after MY… rules)
      if (
        /^(EMPLOYEES SKILLS AUDITS|SKILLS AUDIT )/i.test(code) ||
        /^(SKILLS INVENTORY|SKILL GAP|SKILLS AUDIT M AND E)$/i.test(code) ||
        (name.includes('skills audit') && !/^my\b/i.test(name)) ||
        (name.includes('employees skills audit') || name.includes('skill gap') || name.includes('skills inventory')) ||
        cat.includes('skills audit')
      ) {
        return 'Skills Audit';
      }

      if (/\bGOVERNOR\b/i.test(code) || /\bgovernor\b/i.test(cat) || /\bgovernor\b/i.test(name)) return 'Governor';
      if (code === 'DASHBOARD' || (name === 'dashboard' && !cat.includes('employee'))) return 'Dashboard';

      if (/CABINET|HR_KNOWLEDGE|CS_CORRESPONDENCE|CORRESPONDENCES_VIEW/i.test(code) || /\bcabinet\b|\bcorrespondence\b/i.test(text)) return 'County Secretary';
      if (/COUNTY ATTORNEY|DOCUMENTS FROM ATTORNEY/i.test(code) || cat.includes('attorney') || /\bcounty attorney\b/i.test(text)) return 'County Attorney';

      if (
        /VACANCY|SHORTLIST|INTERVIEW MANAGEMENT|APPOINTMENT MANAGEMENT|CONFIRMATION_PROBATION|ACTING_APPOINTMENT|BOARD_DECISION|RETIREMENT_MANAGEMENT|DISCIPLINARY_HR|SHORT TERM EMPLOYMENT/i.test(code) ||
        /\bvacancy\b|\bshortlist\b|\binterview management\b|\bappointment management\b|\bprobation\b|\bacting appointment\b|\bb board\b|\bpublic service board\b|\bshort term employment\b/i.test(text)
      ) {
        return 'Public Service Board';
      }

      if (
        /EMPLOYEE REGISTER|PENDING APPROVALS|EMPLOYEE APPROVALS|ORG STRUCTURE|POSITION MANAGEMENT|BULK UPLOAD|EMPLOYEE APPROVAL/i.test(code) ||
        (/\bemployee register\b|\borg structure\b|\bdesignation management\b|\bstaff establishment\b|\brecruit\b|\bapproval\b/i.test(text) && !text.includes('compliance'))
      ) {
        return 'Staff Establishment';
      }

      if (cat.includes('attendance') || name.includes('attendance') || name.includes('timesheet') || name.includes('awol')) return 'Attendance Management';
      if (cat.includes('leave') || (name.includes('leave') && !/^MY /i.test(code))) return 'Leave Management';

      if (cat.includes('finance') || name.includes('payroll') || name.includes('imprest') || name.includes('wage bill') || name.includes('deduction') || name.includes('tax')) return 'Finance';

      if ((cat.includes('report') || name.includes('report')) && !/SKILLS AUDIT REPORT/i.test(code)) return 'Reports';

      if (text.includes('poll') || text.includes('announcement') || text.includes('meeting') || text.includes('event') || text.includes('communication')) return 'Communication';

      if (text.includes('document') || text.includes('registry') || text.includes('file registry') || (text.includes('file') && text.includes('document'))) return 'Document Management';

      if (text.includes('system') || text.includes('user management') || text.includes('audit log') || text.includes('category & roles') || text.includes('category and roles') || text.includes('setup & configuration') || text.includes('payroll setup')) return 'System Management';

      if (
        text.includes('compliance unit') ||
        text.includes('attrition') ||
        text.includes('redesignation') ||
        text.includes('redeployment') ||
        text.includes('department management') ||
        text.includes('transfer') ||
        text.includes('promotion') ||
        text.includes('warning') ||
        text.includes('training') ||
        text.includes('knowledge') ||
        text.includes('wealth') ||
        text.includes('pension') ||
        text.includes('gratuity') ||
        text.includes('asset') ||
        text.includes('holiday') ||
        text.includes('performance') ||
        text.includes('kpi') ||
        text.includes('announcements') ||
        text.includes('hr action') ||
        text.includes('education hr')
      ) {
        return 'General HR Actions';
      }

      return 'Uncategorized';
    },
    normalizeSectionName(raw) {
      const key = String(raw || "").trim().toLowerCase();
      if (!key || key === 'other') return 'Uncategorized';

      if (key.includes('governor')) return 'Governor';
      if ((key === 'dashboard' || (key.includes('dashboard') && !key.includes('employee') && !key.includes('my ') && !key.includes('self'))) && !key.includes('governor')) return 'Dashboard';

      if (key.includes('county secretary')) return 'County Secretary';
      if (key.includes('cabinet')) return 'County Secretary';

      if (key.includes('county attorney')) return 'County Attorney';

      if (key.includes('public service board') || (key.includes('public service') && key.includes('board'))) return 'Public Service Board';

      if (key.includes('staff establishment') || key.includes('registry unit')) return 'Staff Establishment';

      if (key.includes('attendance')) return 'Attendance Management';
      if (key.includes('leave')) return 'Leave Management';

      if (key.includes('communication')) return 'Communication';

      if (key.includes('document')) return 'Document Management';

      if (key.includes('skills audit')) return 'Skills Audit';

      if (key.includes('hr actions') || key.includes('general hr') || key.includes('welfare') || key.includes('hr policies')) return 'General HR Actions';

      if (key.includes('finance') || key.includes('claims')) return 'Finance';

      if (key.includes('report')) {
        if (key.includes('skills audit')) return 'Skills Audit';
        return 'Reports';
      }

      if (key.includes('system')) return 'System Management';

      if (key.includes('employee self') || key.includes('self-service') || key.includes('self service')) return 'Employee Self-service';

      return 'Uncategorized';
    },
    isItemSelected(item) {
      const ids = Array.isArray(item?.ids) ? item.ids : [];
      if (!ids.length) return false;
      return ids.every((id) => this.selectedRoles.includes(String(id)));
    },
    upsertSyntheticNavigationItem(grouped, section, title, codes, codeToIds) {
      const ids = [];
      const missingCodes = [];
      (codes || []).forEach((c) => {
        const key = String(c || '').trim().toUpperCase();
        const matched = codeToIds[key] || [];
        if (!matched.length) missingCodes.push(key);
        matched.forEach((id) => ids.push(String(id)));
      });
      const uniqueIds = Array.from(new Set(ids));
      const groupName = section || 'Uncategorized';
      if (!grouped[groupName]) grouped[groupName] = [];
      const key = `${groupName}::${title}`.toLowerCase();
      const existing = grouped[groupName].find((x) => x.key === key);
      const desc = uniqueIds.length
        ? `Navigation: ${title}`
        : `Missing role code(s): ${missingCodes.join(', ')}`;
      if (existing) {
        existing.ids = Array.from(new Set([...(existing.ids || []), ...uniqueIds]));
        if (!existing.ids.length) existing.description = desc;
        return;
      }
      grouped[groupName].push({
        key,
        role_name: title,
        description: desc,
        ids: uniqueIds
      });
    },
    toggleItem(item, checked) {
      const ids = Array.isArray(item?.ids) ? item.ids : [];
      ids.forEach((id) => {
        const roleId = String(id);
        const idx = this.selectedRoles.indexOf(roleId);
        if (checked) {
          if (idx === -1) this.selectedRoles.push(roleId);
        } else if (idx > -1) {
          this.selectedRoles.splice(idx, 1);
        }
      });
    },
    loadCategoryRoles() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getCategoryRoles");
      data.append("categoryId", this.category.id);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            // Handle response properly - check for success first
            if (res && res.data && res.data.success) {
              // Get roles from response
              const categoryRoles = (res.data.data && res.data.data.roles) ? res.data.data.roles : [];
              const allowedIds = new Set(this.normalizedAllRoles.map((r) => String(r.id)));
              const selected = Array.from(new Set(categoryRoles.map((r) => String(r.id))));
              const visibleSelected = selected.filter((id) => allowedIds.has(id));
              const hiddenSelectedCount = selected.length - visibleSelected.length;
              this.selectedRoles = visibleSelected;
              if (hiddenSelectedCount > 0) {
                toast.warning(`${hiddenSelectedCount} inactive/invalid assigned role(s) were ignored. Save to clean them from this profile.`);
              }
            } else {
              // Handle error case
              const errorMsg = (res && res.data && res.data.message) ? res.data.message : 'Failed to load roles';
              console.error("Error loading roles:", errorMsg);
              toast.error(errorMsg);
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error("Error loading category roles:", e);
            toast.error('Error loading category roles: ' + e.message);
          });
    },

    toggleGroupExpand(categoryName) {
      const index = this.expandedGroups.indexOf(categoryName);
      if (index > -1) {
        this.expandedGroups.splice(index, 1);
      } else {
        this.expandedGroups.push(categoryName);
      }
    },

    isGroupFullySelected(roles) {
      return roles.every((role) => this.isItemSelected(role));
    },

    toggleGroupSelection(categoryName, roles) {
      const allSelected = this.isGroupFullySelected(roles);

      roles.forEach(role => {
        this.toggleItem(role, !allSelected);
      });
    },

    selectAllRoles() {
      this.selectedRoles = this.normalizedAllRoles.map(r => String(r.id));
    },

    deselectAllRoles() {
      this.selectedRoles = [];
    },

    saveRoles() {
      this.saving = true;

      const data = new FormData();
      data.append("function", "updateCategoryRoles");
      data.append("categoryId", this.category.id);
      const payloadRoleIds = Array.from(new Set(this.selectedRoles.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)));
      data.append("roleIds", JSON.stringify(payloadRoleIds));

      execute(data, authUrl)
          .then((res) => {
            this.saving = false;

            // Handle response properly
            if (res && res.data && res.data.success) {
              const d = res.data.data || {};
              const persisted = Number(d.persistedRolesCount ?? this.selectedRoles.length);
              toast.success((res.data.message || 'Roles updated successfully') + ` (saved: ${persisted})`);
              this.$emit('saved');
            } else {
              const errorMsg = (res && res.data && res.data.message) ? res.data.message : 'Failed to update roles';
              toast.error(errorMsg);
            }
          })
          .catch((e) => {
            this.saving = false;
            toast.error('Error updating roles: ' + e.message);
          });
    }
  }
};
</script>

<style scoped>
/* FIXED: Proper z-index stacking */
.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
}

.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10000;
}

.modal-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10001 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.modal-dialog {
  pointer-events: auto;
  margin: 0;
}

.modal-content {
  position: relative;
  z-index: 10002;
  background: white;
  border-radius: 0.5rem;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.roles-container {
  max-height: 500px;
  overflow-y: auto;
}

.role-group {
  transition: all 0.3s ease;
}

.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}

.card-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

@media (max-width: 768px) {
  .col-md-6 {
    width: 100%;
  }
}
</style>